/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { MediaSterilization } from "../../../types/mediaBatchSterilization.types";
import { useState } from "react";
import { MediaBatchPreparationInitServices } from "../../../../../../service/mediaBatchPrepInitServices";

const formatTime = (datetimeStr: string) => {
	return datetimeStr.replace("T", " ") + ":00";
};

const extractTime = (value: string): string => {
	if (!value) return "";
	const parts = value.trim().split(" ");
	return parts.length === 2 ? parts[1] : parts[0]; // "16:10:00"
};

const buildSterilizationPayload = (
	batchID: number,
	raw: MediaSterilization,
) => {
	return {
		media_batch_id: batchID,
		autoclave_id: Number(raw.autoclave_id),

		average_pressure_psi: Number(raw.average_pressure_psi),
		hold_duration_min: Number(raw.hold_duration_min),
		max_temperature_observed: Number(raw.max_temperature_observed),

		cycle_start_datetime: formatTime(raw.cycle_start_datetime),
		time_target_temp_attained: extractTime(raw.time_target_temp_attained),
		retained_duration_min: raw.retained_duration_min,
		pressure_release_time: extractTime(raw.pressure_release_time),

		checklist_data: raw.checklist_data,
		steam_clox_indicator_result: raw.steam_clox_indicator_result,
		file_id: raw.file_id,
	};
};

const isFormComplete = (p: MediaSterilization): boolean => {
	return (
		p.autoclave_id !== "" &&
		p.average_pressure_psi !== "" &&
		p.hold_duration_min !== "" &&
		p.max_temperature_observed !== "" &&
		p.cycle_start_datetime !== "" &&
		p.time_target_temp_attained !== "" &&
		p.retained_duration_min !== 0 &&
		p.pressure_release_time !== "" &&
		p.checklist_data.length > 0 &&
		p.steam_clox_indicator_result !== "" &&
		p.file_id !== 0
	);
};

interface Props {
	onBack: () => void;
	onNext: () => void;
	sterilizationPayload: MediaSterilization;
	showToast: (message: string, toastType: "success" | "error") => void;
	isReadonly: boolean;
	batchID: number;
	sterilizationStatus?: string;
	validateFields: () => boolean;
}

const MBSFooter = ({
	onBack,
	onNext,
	sterilizationPayload,
	showToast,
	isReadonly,
	batchID,
	sterilizationStatus,
	validateFields

}: Props) => {
	const { t } = useTranslation();

	const [isSubmitting, setIsSubmitting] = useState(false);
	
	const handleSubmit = async () => {
		if (isReadonly) {
			onNext();
			return;
		}

		if (!validateFields()) {
			return;
		}
		setIsSubmitting(true);

		const payload = buildSterilizationPayload(batchID, sterilizationPayload);
		try {
			const response =
				await MediaBatchPreparationInitServices.postSterilization(payload);
			showToast(
				response?.message || "Media Sterilization Completed Successfully",
				"success"
			);
			setTimeout(() => {
				onNext();
			}, 2500);
			
		} catch (error: any) {
			showToast(
				error?.response?.data?.message || "An unexpected error occurred",
				"error"
			);
		}
	};

	return (
		<div className="bg-white border-t border-slate-200 px-10 py-2 flex items-center justify-between shrink-0 shadow-[0_-20px_40px_-20px_rgba(0,0,0,0.05)]">
			<button
				onClick={onBack}
				className="flex items-center gap-2 px-8 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest hover:text-red-500 transition-all border-none rounded-2xl"
			>
				<span className="material-symbols-outlined text-sm">
					arrow_back
				</span>
				{t("mediaBatchInit.sterilization.backToPreparation")}
			</button>

			<div className="flex items-center gap-12">
				<div className="flex items-center gap-3">
					<span className="material-symbols-outlined text-emerald-500 font-black">
						shield_with_heart
					</span>
					<div className="flex flex-col">
						<span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none mb-1">
							{t("mediaBatchInit.sterilization.safetyLockStatus")}
						</span>
						<span className="text-[10px] font-black text-emerald-600 uppercase tracking-wider">
							{t("mediaBatchInit.sterilization.parameters")}
						</span>
					</div>
				</div>
				{/* <button className="text-[11px] font-black text-slate-400 uppercase tracking-widest hover:text-red-500 transition-colors">
					{t("mediaBatchInit.sterilization.abortCycle")}
				</button> */}
				<button
					onClick={handleSubmit}
					disabled={isSubmitting}
					className="bg-[#FF7043] text-white flex items-center text-[11px] gap-2 px-6 py-2.5 rounded-2xl shadow-xl shadow-orange-500/30 font-black uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all border-none disabled:opacity-50"
				>
					{isSubmitting
						? t("mediaBatchInit.sterilization.submitting")
						: t("mediaBatchInit.sterilization.completeAndDispense")}
					<span className="material-symbols-outlined">arrow_forward</span>
				</button>
			</div>
		</div>
	);
};

export default MBSFooter;
